/**
 * 引擎参数卡、模块列表与通用 UI 控件相关的 UI 文案分区（命名空间 `prompt-tool`）。
 *
 * 约定（与 locales.ts 相同）：zh 是键类型事实源，en 用 Record<keyof typeof zh, string>
 * 强制键集一致；只放用户可见文案，provider/model id、路径、用户内容、协议 code 不进字典。
 */
export const PARAMS_ZH = {
  // 共享参数目录（shared/engine-params.ts）只给键名，显示标签一律 `param.<键>`。
  'param.firstTurnAnchor': '首轮锚定',
  'param.firstTurnText': '首轮锚定文本',
  'param.firstTurnCustom': '使用自定义锚定文本',
  'param.guideText': '每轮引导文本',
  'param.guideCustom': '使用自定义引导文本',
  'param.guideEnabled': '每轮引导',
  'param.injectPrompt': '锚定确认后注入内容',
  'param.modelProvider': '模型服务商',
  'param.modelName': '模型',
  'param.subagentModelProvider': '本地子代理请求服务商',
  'param.subagentModelName': '本地子代理请求模型',
  'param.modelReasoningEffort': '思维程度',
  'param.modelTemperature': '采样温度',
  'param.modelMaxTokens': '输出上限',
  'param.subagentReasoningEffort': '子代理思维程度',
  'param.subagentTemperature': '子代理采样温度',
  'param.subagentMaxTokens': '子代理输出上限',
  'param.maxDepth': '策略委派深度',
  'param.maxDepth.hint': '仅对子代理工具策略生效：需先开启该策略。0 禁止策略委派，正整数限制递归深度，provider-managed 不传深度上限。普通官方委派由宿主管理。',
  'param.firstTurnWord': '锚定确认词',
  'param.buildPattern': '构建任务正则',
  'param.complexPattern': '复杂任务正则',
  'param.firstTurnBuild': '构建档锚句',
  'param.firstTurnInspect': '排查档锚句',
  'param.firstTurnDeep': '深度档锚句',
  'param.guideWeak': '简短引导正文',
  'param.guideDeep': '深度引导正文',
  'param.instructionHint': '指令路径提示',
  'param.toolGitBashEnabled': '启用 Git Bash 工具',
  'param.customToolRequireApproval': '执行前需用户批准的工具种类',

  // 参数卡通用文案。
  'param.hint': '{param}：{label}。空文本/列表回落模块默认；保存后用于后续 generation。',
  'param.inheritPresetDefault': '继承模块默认',
  'param.on': '开启',
  'param.off': '关闭',
  'param.option.either': '工具调用或助手消息',
  'param.option.tool-call': '工具调用',
  'param.option.assistant-message': '助手消息',

  // 能力与模板创建菜单（EngineModuleList）。
  'modules.addTemplates': '添加注入模板',
  'modules.addCapability': '添加能力 / 工具模块',
  'modules.addCapabilityItem': '添加模块 · {id}',
  'modules.createRecipeItem': '连锁创建 · {id}',
  // 共享设置区的能力参数分组标题（按真实能力 card 分组，不另抄键表）。
  'modules.group.str-replace-editor': '编辑器',
  'modules.group.tool-config-engine': '工具配置',
  'modules.group.subagent-delegation': '子代理授权',
  // 层设置区：参数分组标题按 card 派生，能力装配状态与移除入口都在本层设置区内。
  'modules.group.prompt-defaults': '提示词生成默认值',
  'modules.group.main-model': '主模型',
  'modules.group.subagent-model': '子代理模型',
  'modules.group.other': '其他设置',
  'modules.layer.assembled': '本层已装配的能力',
  'modules.layer.asset': '本层的模块资产',
  'modules.layer.capability': '能力：{id}',
  'modules.layer.remove': '移除能力',
  'modules.layer.removeTitle': '移除「{id}」能力？',
  'modules.layer.removeDesc': '从当前模块移除该能力：模块声明与行配置一起移除，它的参数不再生效；其他能力与本层配置不受影响。',
}

export const PARAMS_EN: Record<keyof typeof PARAMS_ZH, string> = {
  'param.firstTurnAnchor': 'First-turn anchor',
  'param.firstTurnText': 'First-turn anchor text',
  'param.firstTurnCustom': 'Use custom anchor text',
  'param.guideText': 'Per-turn guide text',
  'param.guideCustom': 'Use custom guide text',
  'param.guideEnabled': 'Per-turn guide',
  'param.injectPrompt': 'Inject content after anchor confirmation',
  'param.modelProvider': 'Model provider',
  'param.modelName': 'Model',
  'param.subagentModelProvider': 'Local subagent request provider',
  'param.subagentModelName': 'Local subagent request model',
  'param.modelReasoningEffort': 'Reasoning effort',
  'param.modelTemperature': 'Sampling temperature',
  'param.modelMaxTokens': 'Output limit',
  'param.subagentReasoningEffort': 'Subagent reasoning effort',
  'param.subagentTemperature': 'Subagent sampling temperature',
  'param.subagentMaxTokens': 'Subagent output limit',
  'param.maxDepth': 'Policy delegation depth',
  'param.maxDepth.hint': 'Applies only when the subagent tool policy is enabled. 0 forbids policy delegation, a positive integer limits depth, and provider-managed sends no depth cap. The host manages ordinary delegation.',
  'param.firstTurnWord': 'Anchor confirmation word',
  'param.buildPattern': 'Build task pattern',
  'param.complexPattern': 'Complex task pattern',
  'param.firstTurnBuild': 'Build anchor line',
  'param.firstTurnInspect': 'Inspect anchor line',
  'param.firstTurnDeep': 'Deep anchor line',
  'param.guideWeak': 'Short guide text',
  'param.guideDeep': 'Deep guide text',
  'param.instructionHint': 'Instruction path hint',
  'param.toolGitBashEnabled': 'Enable Git Bash tool',
  'param.customToolRequireApproval': 'Tool kinds that need user approval before running',

  'param.hint': '{param}: {label}. Empty text or list falls back to the module default; used for later generations once saved.',
  'param.inheritPresetDefault': 'Inherit module default',
  'param.on': 'On',
  'param.off': 'Off',
  'param.option.either': 'Tool call or assistant message',
  'param.option.tool-call': 'Tool call',
  'param.option.assistant-message': 'Assistant message',

  'modules.addTemplates': 'Add injection template',
  'modules.addCapability': 'Add capability / tool module',
  'modules.addCapabilityItem': 'Add module · {id}',
  'modules.createRecipeItem': 'Create chained modules · {id}',
  'modules.group.str-replace-editor': 'Editor',
  'modules.group.tool-config-engine': 'Tool config',
  'modules.group.subagent-delegation': 'Subagent delegation',
  'modules.group.prompt-defaults': 'Prompt generation defaults',
  'modules.group.main-model': 'Main model',
  'modules.group.subagent-model': 'Subagent model',
  'modules.group.other': 'Other settings',
  'modules.layer.assembled': 'Capabilities mounted in this layer',
  'modules.layer.asset': 'Module assets of this layer',
  'modules.layer.capability': 'Capability: {id}',
  'modules.layer.remove': 'Remove capability',
  'modules.layer.removeTitle': 'Remove the "{id}" capability?',
  'modules.layer.removeDesc': 'Removes this capability from the current module: the module declaration and its row config go away and its parameters stop applying; other capabilities and this layer configs are unaffected.',
}
